import { useState } from "react";
import axios from "axios";
import { CONTACT } from "@/constants/testIds/site";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, MessageCircle, Loader2, CheckCircle2, Camera, X as XIcon, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Resize an image File to a max dimension and return a JPEG data URI
const resizeImage = (file, maxDim = 1600, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [photos, setPhotos] = useState([]); // [{ name, dataUrl }]
  const [photoError, setPhotoError] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const handleFiles = async (files) => {
    setPhotoError("");
    files = Array.from(files || []);
    if (!files.length) return;
    const remaining = 5 - photos.length;
    if (files.length > remaining) {
      setPhotoError(`You can upload up to 5 photos. ${remaining} slot${remaining === 1 ? "" : "s"} left.`);
    }
    const batch = files.slice(0, remaining);
    try {
      const processed = await Promise.all(
        batch.map(async (f) => {
          if (!f.type.startsWith("image/")) throw new Error("not-image");
          if (f.size > 15 * 1024 * 1024) throw new Error("too-large");
          return { name: f.name, dataUrl: await resizeImage(f) };
        })
      );
      setPhotos((p) => [...p, ...processed]);
    } catch (e) {
      if (e.message === "not-image") setPhotoError("Only image files are allowed.");
      else if (e.message === "too-large") setPhotoError("Photos must be under 15MB each.");
      else setPhotoError("Couldn't read one of the photos. Please try again.");
    }
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name || !form.email || !form.message) {
      return setErr("Please fill in all fields.");
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        photos: photos.map((p) => p.dataUrl),
      });
      setSent(true);
    } catch {
      setErr("Couldn't send. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative py-24 md:py-32 px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-emerald-700">
              Get in touch
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
              Questions? <span className="italic font-semibold">We’re here.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg max-w-md">
              Drop us a line and we’ll get back to you the same day — usually
              within a couple of hours.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href="tel:+447497498669"
                data-testid={CONTACT.phone}
                className="flex items-start gap-4 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white text-emerald-700 transition-colors">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase text-slate-500">
                    Mobile / WhatsApp
                  </div>
                  <div className="font-display text-xl font-semibold text-emerald-950 mt-1">
                    07497 498669
                  </div>
                  <div className="text-sm text-slate-600">
                    Available Mon–Sat, 8am–6pm
                  </div>
                </div>
              </a>

              <a
                href="tel:+442030518827"
                data-testid="contact-office-phone"
                className="flex items-start gap-4 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white text-emerald-700 transition-colors">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase text-slate-500">
                    Office
                  </div>
                  <div className="font-display text-xl font-semibold text-emerald-950 mt-1">
                    020 3051 8827
                  </div>
                  <div className="text-sm text-slate-600">
                    Office hours, Mon–Fri
                  </div>
                </div>
              </a>

              <a
                href="mailto:J7GLASS@hotmail.com"
                data-testid={CONTACT.emailLink}
                className="flex items-start gap-4 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white text-emerald-700 transition-colors">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase text-slate-500">
                    Email
                  </div>
                  <div className="font-display text-xl font-semibold text-emerald-950 mt-1 break-all">
                    J7GLASS@hotmail.com
                  </div>
                  <div className="text-sm text-slate-600">
                    For quotes, questions & quick chats
                  </div>
                </div>
              </a>

              <a
                href="https://wa.me/447497498669?text=Hi%20J7%20Collective%20%E2%80%94%20I%E2%80%99d%20like%20a%20quote%20for%20ClearShield%20glass%20protection."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="flex items-start gap-4 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center group-hover:bg-[#25D366] group-hover:text-white text-emerald-700 transition-colors">
                  <MessageCircle size={18} />
                </div>
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase text-slate-500">
                    WhatsApp
                  </div>
                  <div className="font-display text-xl font-semibold text-emerald-950 mt-1">
                    Message us instantly
                  </div>
                  <div className="text-sm text-slate-600">
                    Fastest way to reach the team
                  </div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center text-emerald-700">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase text-slate-500">
                    Service area
                  </div>
                  <div className="font-display text-xl font-semibold text-emerald-950 mt-1">
                    United Kingdom · On‑site
                  </div>
                  <div className="text-sm text-slate-600">
                    We come to you, anywhere in the UK
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-slate-50 border border-slate-200 p-7 md:p-10">
              {sent ? (
                <div
                  data-testid={CONTACT.success}
                  className="text-center py-10"
                >
                  <div className="w-14 h-14 mx-auto rounded-full bg-emerald-600 text-white flex items-center justify-center">
                    <CheckCircle2 size={28} />
                  </div>
                  <h3 className="mt-5 font-display text-3xl font-semibold">
                    Message sent
                  </h3>
                  <p className="mt-3 text-slate-600">
                    Thanks — we’ll be in touch shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="c-name">Name</Label>
                      <Input
                        id="c-name"
                        data-testid={CONTACT.name}
                        value={form.name}
                        onChange={(e) =>
                          setForm({ ...form, name: e.target.value })
                        }
                        placeholder="Your name"
                        className="h-12 bg-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="c-email">Email</Label>
                      <Input
                        id="c-email"
                        data-testid={CONTACT.email}
                        type="email"
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                        placeholder="you@example.com"
                        className="h-12 bg-white"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="c-msg">Message</Label>
                    <Textarea
                      id="c-msg"
                      data-testid={CONTACT.message}
                      value={form.message}
                      onChange={(e) =>
                        setForm({ ...form, message: e.target.value })
                      }
                      placeholder="Tell us about your project…"
                      rows={6}
                      className="bg-white"
                    />
                  </div>

                  {/* Photo upload */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Photos (optional)</Label>
                      <span className="text-xs text-slate-500">
                        {photos.length}/5 · helps us see the glass & access
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* Upload from device */}
                      <label
                        htmlFor="contact-photos-upload"
                        data-testid="contact-photo-upload"
                        className={`flex flex-col items-center justify-center gap-1.5 py-5 px-4 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${
                          photos.length >= 5
                            ? "bg-slate-100 border-slate-200 opacity-60 cursor-not-allowed"
                            : "bg-white border-emerald-200 hover:bg-emerald-50 hover:border-emerald-500"
                        }`}
                      >
                        <Upload size={20} className="text-emerald-700" />
                        <div className="text-sm font-semibold text-emerald-900">
                          Upload from device
                        </div>
                        <div className="text-xs text-slate-500 text-center">
                          Choose existing photos
                        </div>
                        <input
                          id="contact-photos-upload"
                          data-testid="contact-photo-upload-input"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            handleFiles(e.target.files);
                            e.target.value = "";
                          }}
                          disabled={photos.length >= 5}
                          className="hidden"
                        />
                      </label>

                      {/* Take photo (camera) */}
                      <label
                        htmlFor="contact-photos-camera"
                        data-testid="contact-photo-camera"
                        className={`flex flex-col items-center justify-center gap-1.5 py-5 px-4 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${
                          photos.length >= 5
                            ? "bg-slate-100 border-slate-200 opacity-60 cursor-not-allowed"
                            : "bg-white border-emerald-200 hover:bg-emerald-50 hover:border-emerald-500"
                        }`}
                      >
                        <Camera size={20} className="text-emerald-700" />
                        <div className="text-sm font-semibold text-emerald-900">
                          Take a photo
                        </div>
                        <div className="text-xs text-slate-500 text-center">
                          Use your camera now
                        </div>
                        <input
                          id="contact-photos-camera"
                          data-testid="contact-photo-camera-input"
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => {
                            handleFiles(e.target.files);
                            e.target.value = "";
                          }}
                          disabled={photos.length >= 5}
                          className="hidden"
                        />
                      </label>
                    </div>

                    {photoError && (
                      <div className="text-xs text-amber-700 font-medium bg-amber-50 border border-amber-200 px-3 py-2 rounded-xl">
                        {photoError}
                      </div>
                    )}

                    {photos.length > 0 && (
                      <div
                        data-testid="contact-photo-previews"
                        className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 pt-1"
                      >
                        {photos.map((p, i) => (
                          <div
                            key={i}
                            className="relative aspect-square rounded-xl overflow-hidden ring-1 ring-slate-200 group bg-white"
                          >
                            <img
                              src={p.dataUrl}
                              alt={p.name}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(i)}
                              data-testid={`contact-photo-remove-${i}`}
                              aria-label="Remove photo"
                              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-emerald-950/90 text-white flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                            >
                              <XIcon size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {err && (
                    <div className="px-4 py-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
                      {err}
                    </div>
                  )}

                  <button
                    type="submit"
                    data-testid={CONTACT.submit}
                    disabled={sending}
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-950 text-white font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-60"
                  >
                    {sending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      "Send message"
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
